import { Box, Chip, Typography } from '@mui/material';
import CopyId from 'components/common/CopyId';
import { bool, object, shape } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { I18n } from 'utils/i18n';
import employeeShape from 'shapes/employee';
import connector from './connector';
import EmployeeActions from './EmployeeActions';
import InfoList from './InfoList';
import Mandatories from './Mandatories';
import Statistics from './Statistics';

const DOCS_PER_PAGE = 10;

const Profile = ({ employee, actions, authCompany = null, isCompanyAdmin = false }) => {
  const [page, setPage] = useState(0);
  const [signatureStatus, setSignatureStatus] = useState(null);

  const onChangePage = ({ selected } = {}) => {
    setPage(selected);
  };

  const selectStatus = next => {
    setSignatureStatus(next);
    setPage(0);
  };

  useEffect(() => {
    actions.employees.mandatories(
      employee._id,
      page + 1,
      DOCS_PER_PAGE,
      'createdAt',
      'desc',
      signatureStatus || undefined,
    );
  }, [actions.employees, employee._id, page, signatureStatus]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 415,
        width: '100%',
        height: '100%',
        p: '20px 30px 20px 40px',
        overflowY: 'auto',
      }}
    >
      <EmployeeActions
        employee={employee}
        readOnlyAvatar
        authCompany={authCompany}
        isCompanyAdmin={isCompanyAdmin}
        mandatoryReadsTotalCount={employee.mandatories?.totalCount ?? 0}
      />
      <InfoList
        email={employee.email}
        location={employee.location}
        phone={employee.phone}
        tags={employee.tags}
      />
      <Statistics
        mandatoriesCount={employee.isAdmin ? employee.mandatorySent : employee.mandatoryRead}
        messagesSent={employee.messagesSent}
        lastSeen={employee.lastSeen}
      />
      <Box sx={{ marginTop: '20px' }}>
        <Typography variant="caption" gutterBottom>
          {I18n.t('ProfileDrawer.CHANNELS')}
        </Typography>
        {employee.channels?.map(c => (
          <Chip
            label={c.name + (c.description ? ` - ${c.description}` : '')}
            key={c.name}
            sx={{
              height: 35,
              borderRadius: '5px',
              backgroundColor: 'secondary.ultraLight',
              marginRight: '5px',
              marginBottom: '10px',
            }}
          />
        ))}
      </Box>
      {employee.mandatories ? (
        <>
          <Box
            component="hr"
            sx={{
              m: '30px 0 30px 0',
              backgroundColor: 'secondary.extraLight',
              height: '1px',
              border: 0,
              flex: 'none',
            }}
          />
          <Typography variant="subtitle2" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {I18n.t('ProfileDrawer.mandatory reads read')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            {[
              ['all', null, 'ProfileDrawer.signature filter all'],
              ['pending', 'pending', 'ProfileDrawer.signature filter pending'],
              ['signed', 'signed', 'ProfileDrawer.signature filter signed'],
            ].map(([key, value, label]) => (
              <Chip
                key={key}
                label={I18n.t(label)}
                clickable
                color={signatureStatus === value ? 'primary' : 'default'}
                onClick={() => selectStatus(value)}
              />
            ))}
          </Box>
          {employee.mandatories.totalCount ? (
            <Mandatories
              employee={employee}
              total={employee.mandatories.totalCount}
              documents={employee.mandatories.mandatoryRead}
              page={page}
              perPage={DOCS_PER_PAGE}
              changePage={onChangePage}
            />
          ) : null}
        </>
      ) : null}

      {!employee.user_id || <CopyId id={employee.user_id} sx={{ paddingTop: '20px' }} />}
    </Box>
  );
};

Profile.propTypes = {
  employee: employeeShape.isRequired,
  actions: shape({
    employees: object.isRequired,
  }).isRequired,
  authCompany: object,
  isCompanyAdmin: bool,
};

export default connector(Profile);
